import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, catchError, of } from 'rxjs';
import { ExpenseEditPageInfo, GetExpenseCategory_Result, EXGetReceiptLines_Result } from '@typeDefs/expenses/responses/expenses';
import { FileAttachmentGetList_Result, HRUser, HRAttachmentsBySubmissionID_Result } from '@typeDefs/employee/responses/employee';
import { ApiResponse, ApiDataResponse } from '@shared/model/api-response.model';
import { ADGetJobsByAuthToken_Result, ADGetUsersByPRCoAndPRGroup_Result } from '@typeDefs/admin/responses/admin';
import { SimpleLookupResult } from '@typeDefs/general/general';
import { EarningsDocumentDownload_Request } from '@typeDefs/document/requests/document';
import { EmployeeInfo } from '@typeDefs/home/responses/home';

@Injectable({
  providedIn: 'root',
})
export class EmployeeApiService {
  private readonly http = inject(HttpClient);

  getEmployeeInfo(): Observable<EmployeeInfo> {
    return this.http.get<EmployeeInfo>('/Employee/GetInfo');
  }

  getCategory(): Observable<GetExpenseCategory_Result[]> {
    const url = '/Employee/CatgGet';
    return this.http.get<GetExpenseCategory_Result[]>(url);
  }

  catgAddEdit(expenseCatgID: number, expenseCatgName: string, isActive: boolean): Observable<ApiResponse> {
    return this.http.post<ApiResponse>('/Employee/CatgAddEdit', {
      expenseCatgID: expenseCatgID,
      expenseCatgName: expenseCatgName,
      isActive: isActive,
    });
  }

  checkForDuplicateCCs(searchText: string): Observable<ApiDataResponse<HRUser[]>> {
    const params = new HttpParams().set('lastFourCCDigits', searchText);
    return this.http.get<ApiDataResponse<HRUser[]>>('/Employee/CheckForDuplicateCCs', { params: params });
  }

  catgDelete(expenseCatgID: number): Observable<ApiResponse> {
    return this.http.post<ApiResponse>('/Employee/CatgDelete', {
      expenseCatgID: expenseCatgID,
    });
  }

  getFileAttachmentBinary(fileAttachmentID: string): Observable<string> {
    const url = '/Employee/GetFileAttachmentBinary';
    const params = new HttpParams().set('fileAttachmentID', fileAttachmentID);
    return this.http.get<string>(url, { params: params });
  }

  getFileAttachmentList(
    moduleID: number,
    recordID: number,
    onboarding: boolean,
    recordGuid: string
  ): Observable<FileAttachmentGetList_Result[]> {
    const url = '/Employee/GetFileAttachmentList';
    let params = new HttpParams();
    if (moduleID) params = params.set('moduleID', moduleID);
    if (recordID) params = params.set('recordID', recordID);
    params = params.set('onboarding', onboarding);
    if (recordGuid) params = params.set('recordGuid', recordGuid);
    return this.http.get<FileAttachmentGetList_Result[]>(url, { params });
  }

  getFileAttachmentData(fileAttachmentID: string): Observable<Blob> {
    const url = '/Employee/GetFileAttachmentData';
    const params = new HttpParams().set('fileAttachmentID', fileAttachmentID);

    return this.http.get(url, { params: params, responseType: 'blob' });
  }

  removeFileAttachment(fileAttachmentID: number): Observable<ApiResponse> {
    return this.http.post<ApiResponse>('/Employee/RemoveFileAttachment', {
      fileAttachmentID: fileAttachmentID,
    });
  }

  updateHRUser(user: ADGetUsersByPRCoAndPRGroup_Result): Observable<ApiDataResponse<{ matchingCCUsers: string }>> {
    return this.http.post<ApiDataResponse<{ matchingCCUsers: string }>>('/Employee/UpdateHRUser', user);
  }

  getFilingStatus(useConfigSetting?: boolean): Observable<SimpleLookupResult[]> {
    let params = new HttpParams();
    if (useConfigSetting !== undefined) {
      params = params.set('useConfigSetting', String(useConfigSetting));
    }
    return this.http.get<SimpleLookupResult[]>('/Employee/GetFilingStatus', { params });
  }

  getAttachmentsBySubmissionID(submissionGuid: string): Observable<HRAttachmentsBySubmissionID_Result[]> {
    const url = '/Employee/GetAttachmentsBySubmissionID';
    const params = new HttpParams().set('submissionGuid', submissionGuid);
    return this.http.get<HRAttachmentsBySubmissionID_Result[]>(url, { params });
  }

  updateServiceHubAdmins(user: ADGetUsersByPRCoAndPRGroup_Result): Observable<ApiResponse> {
    return this.http.post<ApiResponse>('/Employee/UpdateServiceHubAdmins', user);
  }

  sendFileAttachmentRawDataToVP(
    moduleID: number,
    recordKeyID: number,
    fileAttachmentType: string,
    submissionGuid?: any,
    descriptionList?: string,
    fileDescription?: string,
    attachmentType?: string
  ): Observable<ApiResponse> {
    return this.http.post<ApiResponse>('/Document/SendFileAttachmentRawDataToVP', {
      moduleID: moduleID,
      recordKeyID: recordKeyID,
      fileAttachmentType: fileAttachmentType,
      submissionGuid: submissionGuid,
      descriptionList: descriptionList,
      fileDescription: fileDescription,
      attachmentType: attachmentType,
    });
  }

  getDocumentForViewByDocument(document: any, downloadID: string): Observable<string> {
    const params = this.buildDocumentViewParams(document, downloadID);
    return this.http.get<string>('/Document/GetFileView', {
      params: params,
    });
  }

  downloadDocuments(request: EarningsDocumentDownload_Request): Observable<ApiResponse> {
    const url = '/Document/DownloadDocuments';
    return this.http.post<ApiResponse>(url, request);
  }

  downloadSingleDocument(request: EarningsDocumentDownload_Request): Observable<Blob> {
    const url = '/Document/DownloadDocuments';
    return this.http.post(url, request, {
      responseType: 'blob',
    });
  }

  /**
   * Builds HttpParams for document viewing based on document type
   */
  private buildDocumentViewParams(document: any, downloadID: string): HttpParams {
    let params = new HttpParams()
      .set('prehKeyId', document.PREHKeyId)
      .set('docType', document.DocCode)
      .set('pRCo', document.PRCo)
      .set('downloadID', downloadID);

    const yearBasedDocs = ['W2', 'T4', 'COMP', '1095C', 'PAYG'];

    if (yearBasedDocs.includes(document.DocCode)) {
      params = params.set('year', document.Year.toString()).set('filename', document.FileName);

      if (document.AttachmentId) {
        params = params.set('attachmentId', document.AttachmentId);
      }
    } else if (document.DocCode === 'PR') {
      if (document.AttachmentId) {
        params = params.set('attachmentId', document.AttachmentId);
      }
    } else if (document.DocCode === 'TCS' || document.DocCode === 'TCP') {
      params = params.set('prpcKeyId', document.PRPCKeyId).set('filename', document.FileName).set('processedOrSubmitted', document.DocCode);
      if (document.HQBCKeyId) params = params.set('hqbcKeyId', document.HQBCKeyId);

      if (document.SubmittedByPREHKeyID) {
        params = params.set('submittedBy', document.SubmittedByPREHKeyID);
      }
    }

    return params;
  }
  getExpenseEditPageInfo(): Observable<ExpenseEditPageInfo> {
    return this.http
      .get<ExpenseEditPageInfo>('/Employee/GetExpenseEditPageInfo')
      .pipe(catchError(() => of({ umList: [], categories: [], APCustomFieldDefinitions: [], PRCustomFieldDefinitions: [] })));
  }

  getExpenseReceiptLines(receiptIDString: string, fromApproval = false): Observable<EXGetReceiptLines_Result[]> {
    return this.http.post<EXGetReceiptLines_Result[]>('/Employee/GetExpenseReceiptLines', {
      receiptIDString: receiptIDString,
      fromApproval: fromApproval,
    });
  }

  getJobs(form: string, searchString: string): Observable<ADGetJobsByAuthToken_Result[]> {
    const url = '/Employee/GetJobs';
    const params = new HttpParams().set('form', form).set('searchString', searchString);
    return this.http.get<ADGetJobsByAuthToken_Result[]>(url, { params });
  }
}
